
import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Coverage, IFutureCoverageRequest, MemberDetails } from '@interfaces';
import { EligibilityService } from 'src/app/core/services/eligibility/eligibility.service';
import { AppConstants } from 'src/app/app.constants';
import {
  AuthService,
  ErrorManagerService,
  ErrorType,
} from '@core';
import { getErrorMessage } from 'src/app/util';
import { finalize } from 'rxjs/operators';
import { LoaderService } from '../../../services/loader/loader.service';
import { AppCenterAnalytics } from 'src/app/services/app-insights/app-insights';
import { AppInsightsService } from '@services';

@Component({
  selector: 'app-apply-cardholder-future-coverage-dialog',
  templateUrl: './apply-cardholder-future-coverage-dialog.component.html',
  styleUrls: ['./apply-cardholder-future-coverage-dialog.component.scss'],
  standalone: false
})
export class ApplyCardholderFutureCoverageDialog
  implements OnInit {
  displayedColumns: string[] = [
    'personCode',
    'name',
    'cardId',
    'relationshipCode',
    'startDate',
    'endDate',
  ];

  dataSource: MemberDetails[] = [];
  displayGender = AppConstants.DISPLAY_GENDER;

  constructor(
    private readonly dialogRef: MatDialogRef<ApplyCardholderFutureCoverageDialog>,
    @Inject(MAT_DIALOG_DATA)
    public dialogData: {
      cardholderDetails: MemberDetails;
      cardHolderFutureCoverage: Coverage;
      dependentDetails: MemberDetails;
    },
    private readonly eligibilityService: EligibilityService,
    private readonly authService: AuthService,
    private readonly loader: LoaderService,
    private readonly errorManager: ErrorManagerService,
    private readonly appInsightService: AppInsightsService
  ) { }

  ngOnInit(): void {
    this.appInsightService.trackPageView(AppCenterAnalytics.APPLY_CARDHOLDER_FUTURE_COVERAGE_FOR_DEPENDENT_DIALOG_VIEWED);
    // Populate the table with cardholder details and future coverage information
    this.initializeTableData();
  }

  cancel(): void {
    this.appInsightService.trackPageView(AppCenterAnalytics.APPLY_CARDHOLDER_FUTURE_COVERAGE_FOR_DEPENDENT_DIALOG_CLOSED);
    this.dialogRef.close();
  }

  addCoverageForDependent(): void {
    this.appInsightService.trackPageView(AppCenterAnalytics.APPLY_CARDHOLDER_FUTURE_COVERAGE_FOR_DEPENDENT_DIALOG_SUBMIT);
    this.loader.showLoader();
    const request = this.buildFutureCoverageRequest();
    this.eligibilityService
      .addMemberFutureCoverage(request)
      .pipe(finalize(() => this.loader.hideLoader()))
      .subscribe({
        next: (res) => {
          this.appInsightService.trackPageView(AppCenterAnalytics.APPLY_CARDHOLDER_FUTURE_COVERAGE_FOR_DEPENDENT_API_SUCCESS, { response: res });
          this.dialogRef.close();
        },
        error: (err) => this.handleError(err),
      });
  }

  private initializeTableData(): void {
    const memberDetails = this.dialogData.cardholderDetails;
    memberDetails.coverage.startDate = this.dialogData.cardHolderFutureCoverage.startDate;
    memberDetails.coverage.endDate = this.dialogData.cardHolderFutureCoverage.endDate;
    memberDetails.coverage.groupId = this.dialogData.cardHolderFutureCoverage.groupId;
    memberDetails.relationshipValue = AppConstants.RELATIONSHIP_VALUES[memberDetails.relationshipCode];
    this.dataSource = [memberDetails];
  }

  private buildFutureCoverageRequest(): IFutureCoverageRequest {
    const { cardholderDetails, dependentDetails, cardHolderFutureCoverage } =
      this.dialogData;
    return {
      cardId: cardholderDetails.cardId,
      clientId: cardholderDetails.clientId,
      empirxMemberId: cardholderDetails.empirxMemberId ?? '',
      startDate: cardHolderFutureCoverage.startDate,
      endDate: cardHolderFutureCoverage.endDate,
      groupId: cardHolderFutureCoverage.groupId,
      personCodes: [dependentDetails.personCode],
      userName: this.authService.userEmail,
    };
  }

  private handleError(err: unknown): void {
    const message = getErrorMessage(err);
    this.appInsightService.trackPageView(AppCenterAnalytics.APPLY_CARDHOLDER_FUTURE_COVERAGE_FOR_DEPENDENT_API_ERROR, { error: err });
    this.errorManager.throwError({
      type: ErrorType.ERROR_GENERIC,
      message,
      component: 'Update Coverage Details',
      title: '',
    });

    this.dialogRef.close();
  }
}
